import {
  Component, OnInit, AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy, ViewChild, ElementRef
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { Store } from '@ngrx/store';

import { ReplaySubject, combineLatest, merge, Observable } from 'rxjs';
import { takeUntil, map } from 'rxjs/operators';

import { Selectors, AppState, Actions } from 'store/root-reducer';
import { CustomerActionTypes } from 'store/actions/customer.actions';
import { RecordActionTypes } from 'store/actions/record.action';
import { SearchActionTypes } from 'store/actions/search.actions';

import { Customer } from 'modules/shared/models/customer.models';
import { RecordType } from 'modules/shared/models/record.models';
import { PostScreeningRequest } from 'store/models/screening.models';
import { CrpPayload } from 'store/models/crp.models';
import { IndividualRecord, CorporateRecord } from 'modules/shared/models/record.models';
import { GetPrimaryCrpRecordPayload, RecordOtherDataResponse } from 'store/models/record.models';
import { CreateOrUpdateCustomerRequest, CreateOrUpdateCustomerPayload } from 'store/models/customer.models';
import { CheckBalancePayload } from 'store/models/billable.models';

import { IBreadcrumbs } from 'modules/shared/components/breadcrumbs/breadcrumbs.component';

import { CustomersBase } from '../../customers-base';
import { CustomerService } from 'modules/core/services/customer.service';

@Component({
  selector: 'artemis-customer-edit-page',
  templateUrl: './customer-edit.page.html',
  styleUrls: ['./customer-edit.page.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CustomerEditPage extends CustomersBase implements OnInit, OnDestroy, AfterViewInit {

  @ViewChild('customerDocument', {static: true}) customerDocument: ElementRef;

  private unsubscribe$: ReplaySubject<any> = new ReplaySubject<any>(1);
  customer: { record: IndividualRecord | CorporateRecord, customer: Customer };
  individualForm: any;
  corporateForm: any;
  individualFormObject: FormGroup;
  corporateFormObject: FormGroup;
  customerType: any;
  individualFormValid: boolean = false;
  corporateFormValid: boolean = false;

  triedSubmit: boolean = false;
  dataAvaliableFormFields: any;

  breadcrumbs: Observable<Array<IBreadcrumbs>> = this.store.select(Selectors.customer.getCustomer)
    .pipe(
      takeUntil(this.unsubscribe$),
      map((customer: Customer) => {
        if (customer) {
          return [
            {
              url: '/customers',
              label: 'Customer List'
            }, {
              url: `/customers/${customer.id}`,
              label: customer.name
            }, {
              url: `/customers/${customer.id}/edit`,
              label: 'Edit'
            }
          ];
        } else {
          return [];
        }
      })
    );

  formLoading: boolean = false;
  customerDataLoading$: Observable<boolean>;

  otherData: RecordOtherDataResponse;

  constructor(private router: Router,
              private activatedRoute: ActivatedRoute,
              private cdRef: ChangeDetectorRef,
              private customerService: CustomerService,
              private store: Store<AppState>) {
    super();
  }

  ngOnInit() {
    this.getLoadingStatus();

    combineLatest([
      merge(
        this.store.select(Selectors.record.getCorporateRecord),
        this.store.select(Selectors.record.getIndividualRecord)
      ),
      this.store.select(Selectors.customer.getCustomer)
    ]).pipe(
      takeUntil(this.unsubscribe$)
    ).subscribe(([record, customer]: [IndividualRecord | CorporateRecord, Customer]) => {
      if (record && customer) {
        this.customer = { record, customer };
      }

      this.cdRef.detectChanges();
    }, () => {
      this.cdRef.detectChanges();
    });

    this.store.dispatch(new Actions.record.GetPrimaryCrpRecord(<GetPrimaryCrpRecordPayload>{
      customerId: this.activatedRoute.snapshot.params['id'],
      getOtherData: true
    }));

    this.store.select(Selectors.record.getRecordOtherData)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((res: RecordOtherDataResponse) => {
        if (this.hasValidOtherData(res)) {
          this.otherData = res;
        } else {
          this.otherData = null;
        }
      });

      this.customerService.getAppConfig()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(item => {
        this.dataAvaliableFormFields = item;
      });
  }

  ngAfterViewInit() {
    this.activatedRoute
      .fragment
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (fragment: string) => {
          if (fragment) {
            document.querySelector('#' + fragment).scrollIntoView();
          }
        }
      );
  }

  /**
   * Get loading status from store
   * @method getLoadingStatus
   */
  private getLoadingStatus(): void {
    this.customerDataLoading$ = combineLatest([
      this.store.select(Selectors.customer.getLoading),
      this.store.select(Selectors.record.getLoading),
      this.store.select(Selectors.search.getLoading)
    ]).pipe(
      takeUntil(this.unsubscribe$),
      map(([customerLoading, recordLoading, screeningLoading]:
           [Array<CustomerActionTypes>, Array<RecordActionTypes>, Array<SearchActionTypes>]) => {
        return customerLoading.includes(CustomerActionTypes.GET_CUSTOMER) ||
               customerLoading.includes(CustomerActionTypes.CREATE_AND_CRP_CUSTOMER) ||
               customerLoading.includes(CustomerActionTypes.CREATE_AND_RA_CUSTOMER) ||
               customerLoading.includes(CustomerActionTypes.CREATE_OR_UPDATE_CUSTOMER) ||
               recordLoading.includes(RecordActionTypes.GET_OTHER_DATA) ||
               recordLoading.includes(RecordActionTypes.GET_INDIVIDUAL_RECORD) ||
               recordLoading.includes(RecordActionTypes.GET_CORPORATE_RECORD) ||
               screeningLoading.includes(SearchActionTypes.POST_SCREENING);
      })
    );
  }

  private getCustomerObject(): any {
    let recordPayload;

    const formName: string = this.customerType === RecordType.INDIVIDUAL ? 'individualForm' : 'corporateForm';
    const customerDataToSend: CreateOrUpdateCustomerRequest = <CreateOrUpdateCustomerRequest>{
      paymentModes: this[formName].paymentModes,
      onboardingMode: this[formName].onboardingMode,
      productServiceComplexity: this[formName].productServiceComplexity,
      natureOfBusinessRelationship: this[formName].natureOfBusinessRelationship,
      referenceId: this[formName].customerReferenceId,
      isActiveCustomer: this[formName].isActiveCustomer,
      users: this.customer.customer.users,
      domains: this.customer.customer.domains,
    };

    if (this.customerType === RecordType.INDIVIDUAL) {
      recordPayload = {
        id: this.customer.record.id,
        title: this.individualForm.title,
        name: this.individualForm.name,
        aliasNames: this.individualForm.aliasNames,
        oldNames: this.individualForm.oldNames,
        nationality: this.individualForm.nationality,
        countryOfResidence: this.individualForm.countryOfResidence,
        gender: this.individualForm.gender,
        dateOfBirth: this.individualForm.dateOfBirth,
        industry: this.individualForm.industry,
        occupation: this.individualForm.occupation,
        addresses: this.individualForm.addresses,
        phoneNumbers: this.individualForm.phoneNumbers,
        referenceId: this.individualForm.recordReferenceId,
        sourceOfFunds: this.individualForm.sourceOfFunds,
        emailAddresses: this.individualForm.emailAddresses,
        bankAccounts: this.individualForm.bankAccounts,
        countryOfBirth: this.individualForm.countryOfBirth,
        idType: this.individualForm.idType,
        idNumber: this.individualForm.idNumber,
        idIssueDate: this.individualForm.idIssueDate,
        idExpiryDate: this.individualForm.idExpiryDate,
        primary: true
      };
      customerDataToSend['sourceOfWealth'] = this.individualForm.sourceOfWealth;
      customerDataToSend['annualIncomeLevel'] = this.individualForm.annualIncomeLevel;
      customerDataToSend['policyCount'] = this.individualForm.policyCount;
      customerDataToSend['premiumAmount'] = this.individualForm.premiumAmount;
      customerDataToSend.individualRecords = [recordPayload];
    }

    if (this.customerType === RecordType.CORPORATE) {
      recordPayload = {
        id: this.customer.record.id,
        isIncorporated: !!this.corporateForm.isIncorporated,
        name: this.corporateForm.name,
        aliasNames: this.corporateForm.aliasNames,
        oldNames: this.corporateForm.oldNames,
        entityType: this.corporateForm.entityType,
        ownershipStructureLayers: this.corporateForm.ownershipStructureLayers,
        countryOfIncorporation: this.corporateForm.countryOfIncorporation,
        countryOfOperations: this.corporateForm.countryOfOperations,
        businessActivity: this.corporateForm.businessActivity,
        addresses: this.corporateForm.addresses,
        phoneNumbers: this.corporateForm.phoneNumbers,
        referenceId: this.corporateForm.recordReferenceId,
        sourceOfFunds: this.corporateForm.sourceOfFunds,
        emailAddresses: this.corporateForm.emailAddresses,
        bankAccounts: this.corporateForm.bankAccounts,
        incorporationNumber: this.corporateForm.incorporationNumber,
        website: this.corporateForm.website,
        incorporationDate: this.corporateForm.incorporationDate,
        incorporationExpiryDate: this.corporateForm.incorporationExpiryDate,
        inFatfJurisdiction: this.corporateForm.inFatfJurisdiction,
        imoNumber: this.corporateForm.imoNumber,
        primary: true
      };
      customerDataToSend.corporateRecords = [recordPayload];
      customerDataToSend['policyCount'] = this.corporateForm.policyCount;
      customerDataToSend['premiumAmount'] = this.corporateForm.premiumAmount;
    }

    return <CreateOrUpdateCustomerPayload>{
      customerData: customerDataToSend,
      customerId: this.customer.customer.id,
      mode: 'UPDATE'
    };
  }

  // Get CRPS
  getCRPS(): void {
    this.store.dispatch(new Actions.customer.GetCrps(<CrpPayload>{
      customerId: this.customer.customer.id
    }));
    this.customerDocument.nativeElement.scrollIntoView({ behavior: 'smooth' });
  }

  isFormValid(): boolean {
    return (this.customerType === RecordType.INDIVIDUAL && this.individualFormValid) ||
      (this.customerType === RecordType.CORPORATE && this.corporateFormValid);
  }

  isFormDirty(): boolean {
    return (this.customerType === RecordType.INDIVIDUAL && this.individualFormObject.dirty) ||
      (this.customerType === RecordType.CORPORATE && this.corporateFormObject.dirty);
  }

  /**
   * Check if user has entered any alias name or former registered name
   * @method hasFormerOrAliasName
   */
  hasFormerOrAliasName(): boolean {
    if (this.customerType === RecordType.INDIVIDUAL) {
      return this.individualForm.aliasNames.length || this.individualForm.oldNames.length;
    } else if (this.customerType === RecordType.CORPORATE) {
      return this.corporateForm.aliasNames.length || this.corporateForm.oldNames.length;
    }

    return false;
  }

  updateCustomer(subaction?: string) {
    this.triedSubmit = true;
    if (this.isFormDirty() && this.isFormValid()) {
      if (subaction === 'ra') {
        this.store.dispatch(new Actions.billable.CheckBalance(<CheckBalancePayload>{
          actions: [
            new Actions.customer.CreateAndRACustomer(this.getCustomerObject())
          ]
        }));
      } else if (subaction === 'crp' && this.customerType === RecordType.CORPORATE) {
        this.store.dispatch(new Actions.customer.CreateAndCRPCustomer(this.getCustomerObject()));
      } else {
        this.store.dispatch(new Actions.customer.CreateOrUpdateCustomer(this.getCustomerObject()));
      }
    } else if (this.isFormValid()) {
      if (subaction === 'ra') {
        this.store.dispatch(new Actions.billable.CheckBalance(<CheckBalancePayload>{
          actions: [
            new Actions.search.PostScreening(<PostScreeningRequest>{
              customer: this.customer.customer,
              generateReport: true,
              invalidOnly: true,
              redirectToScreening: true
            })
          ]
        }));
      } else if (subaction === 'crp' && this.customerType === RecordType.CORPORATE) {
        this.router.navigate([`/customers/${this.customer.customer.id}/edit/crp-add`], { fragment: 'addCrpCard' });
      }
    }
  }

  formChange(data: any) {
    this.individualForm = data.individualForm;
    this.corporateForm = data.corporateForm;
    this.customerType = data.customerType;
    this.individualFormValid = data.individualFormValid;
    this.corporateFormValid = data.corporateFormValid;
    this.individualFormObject = data.individualFormObject;
    this.corporateFormObject = data.corporateFormObject;
  }

  formLoadingHandler(formLoading: boolean): void {
    this.formLoading = formLoading;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.store.dispatch(new Actions.record.ResetRecord());
    this.store.dispatch(new Actions.customer.ResetCustomerOverview());
  }
}
